/* Card quick add as a bottom sheet on phones (Theme settings > Product cards > Quick add on phones = Bottom sheet).
   Loaded after advanced-base.js and sg-qa-options.js only when that setting is on. The card's own quick add dialog is
   opened as a modal (top layer) and restyled in design-system.css; sg-qa-options.js adds the header (photo, title,
   price, link) and mirrors the theme's option dropdowns as swatches / option buttons, so the theme's variant logic,
   prices and Add to cart keep working unchanged. */
(() => {
  const Base = customElements.get('advanced-modal');
  if (!Base || Base.prototype.sgSheetPatched) return;
  Base.prototype.sgSheetPatched = true;

  const phone = window.matchMedia('(max-width: 749px)');
  const open = Base.prototype.popupOpen;
  const close = Base.prototype.popupClose;

  const wantsSheet = (modal) =>
    phone.matches &&
    modal.localName === 'product-modal' &&
    modal.type === 'cart-item' &&
    modal.popup &&
    modal.closest('.product-card-wrapper');

  Base.prototype.popupOpen = function () {
    open.call(this);
    const popup = this.popup;
    // The dialog lives inside the card's swiper-slide, so the carousel behind took every sideways swipe on it and
    // cancelled the photo strip's scroll (Van 2026-10-23, iPhone 12: More sets sheet photos would not scroll).
    // Swiper ignores touches that start inside .swiper-no-swiping.
    popup?.classList.add('swiper-no-swiping');
    if (!wantsSheet(this)) return;

    // Content is re-rendered on every open, so the header and option buttons are rebuilt too
    const card = this.closest('.product-card-wrapper');
    window.sgQaOptions?.header(popup, {
      link: card.querySelector('.card__heading a, a.full-unstyled-link'),
      image: card.querySelector('.card__media img'),
      price: card.querySelector('.card-information .price, .price'),
    });
    window.sgQaOptions?.build(popup);
    window.sgQaOptions?.gallery?.(popup, {
      link: card.querySelector('.card__heading a, a.full-unstyled-link'),
      images: [...card.querySelectorAll('.card__media img')],
    });
    if (popup.classList.contains('sg-qa-sheet--closing')) {
      popup.sgCloseToken = (popup.sgCloseToken || 0) + 1;
      popup.classList.remove('sg-qa-sheet--closing');
    }
    if (popup.matches(':modal')) return;

    popup.removeAttribute('open');
    popup.classList.add('sg-qa-sheet');
    lockTouchScroll(popup);
    try {
      popup.showModal();
    } catch (error) {
      popup.setAttribute('open', '');
    }
    popup.scrollTop = 0; // older browsers without overflow: clip (design-system.css)
    popup.querySelector('.advanced-modal__close')?.focus({ preventScroll: true });
  };

  // The page behind stays put without overflow: hidden on <html>, which re-laid out the whole collection page while
  // the sheet slid up (the jank Van saw). Drags on the dim area, or on a sheet too short to scroll, are cancelled.
  function lockTouchScroll (popup) {
    if (popup.sgTouchLock) return;
    popup.sgTouchLock = true;
    popup.addEventListener('touchmove', (event) => {
      if (!popup.classList.contains('sg-qa-sheet')) return;
      const content = popup.querySelector('.advanced-modal__content');
      if (!content || !content.contains(event.target)) {
        event.preventDefault();
        return;
      }
      if (event.target.closest('.sg-qa-sheet__gallery')) return;
      if (content.scrollHeight <= content.clientHeight + 1) event.preventDefault();
    }, { passive: false });
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  Base.prototype.popupClose = function () {
    const popup = this.popup;
    if (popup?.classList.contains('sg-qa-sheet')) {
      if (popup.classList.contains('sg-qa-sheet--closing')) return;
      const token = (popup.sgCloseToken = (popup.sgCloseToken || 0) + 1);
      const finish = () => {
        if (popup.sgCloseToken !== token) return; // reopened meanwhile
        popup.classList.remove('sg-qa-sheet', 'sg-qa-sheet--closing');
        if (popup.open) popup.close(); // the theme's 'close' listener finishes the job (inert, events)
        this._startSwiper?.();
      };
      const content = popup.querySelector('.advanced-modal__content');
      if (!popup.open || !content || reducedMotion.matches) {
        finish();
        return;
      }
      // slide down first (design-system.css), then close
      popup.classList.add('sg-qa-sheet--closing');
      const onEnd = (event) => {
        if (event.target !== content) return;
        content.removeEventListener('animationend', onEnd);
        finish();
      };
      content.addEventListener('animationend', onEnd);
      setTimeout(finish, 260);
      return;
    }
    close.call(this);
  };

  // Adding to cart opens the cart drawer, which the sheet (top layer) would cover.
  document.addEventListener('ajaxProduct:added', () => {
    document.querySelectorAll('dialog.sg-qa-sheet[open]').forEach((popup) => {
      const modal = document.querySelector(`product-modal[data-id="${popup.dataset.target}"]`);
      modal ? modal.popupClose() : popup.close();
    });
  });

  // Prefetch (Van 2026-10-22 "make sure all modals load fast, no delay"; Pavoi's quick add opens with no request): the
  // quick view HTML and the product's photo list are fetched quietly once a card is on screen, then stored where the
  // theme's product-modal looks first (productElements), so a tap opens the sheet straight away. About 5 KB a card;
  // skipped on Save-Data / 2G, where only a press (touchstart / hover) prefetches.
  const prefetched = new WeakSet();
  const modalUrl = (modal) => modal.buttonPopupOpen?.href ?? modal.buttonPopupOpen?.dataset?.href;

  function prefetch (modal) {
    const url = modalUrl(modal);
    if (!url || modal.disabled || !modal.productElements || modal.productElements[url] || prefetched.has(modal)) return;
    prefetched.add(modal);
    const handle = (modal.closest('.product-card-wrapper, .sg-ctl__row')?.querySelector('a[href*="/products/"]')?.getAttribute('href')?.match(/\/products\/([^/?#]+)/) || [])[1];
    if (handle) window.sgQaOptions?.photos?.(handle);
    fetch(url)
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        if (!text || modal.productElements[url]) return;
        const productTemplate = new DOMParser().parseFromString(text, 'text/html').querySelector('product-template');
        if (!productTemplate) return;
        // same preparation as ProductModal.popupOpen in advanced-base.js
        productTemplate.id = productTemplate.id.replace('ProductSection', 'QuickModalSection');
        productTemplate.removeAttribute('data-enable-history-state');
        productTemplate.setAttribute('disabled', 'true');
        const element = modal.removeDOMElements(productTemplate.closest('.shopify-section'));
        if (modalUrl(modal) === url) modal.productElements[url] = element.innerHTML;
      })
      .catch(() => prefetched.delete(modal));
  }

  const modalSelector = '.product-card-wrapper product-modal, .sg-ctl__add product-modal'; // cards + Complete the look rows
  const modalFrom = (target) => target instanceof Element && target.closest(modalSelector);
  const onPress = (event) => {
    const modal = modalFrom(event.target);
    if (modal) prefetch(modal);
  };
  document.addEventListener('touchstart', onPress, { capture: true, passive: true });
  document.addEventListener('pointerover', (event) => { if (event.pointerType === 'mouse') onPress(event); }, { passive: true });

  const connection = navigator.connection;
  const lowData = () => Boolean(connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || ''));
  if (!('IntersectionObserver' in window)) return;
  const queue = [];
  let running = 0;
  const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 200));
  const pump = () => {
    while (running < 2 && queue.length) {
      const modal = queue.shift();
      if (!modal.isConnected) continue;
      running += 1;
      idle(() => {
        prefetch(modal);
        running -= 1;
        pump();
      }, { timeout: 2000 });
    }
  };
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      if (lowData()) return;
      queue.push(entry.target);
    });
    pump();
  }, { rootMargin: '200px 0px' });
  const watched = new WeakSet();
  const scan = () => {
    document.querySelectorAll(modalSelector).forEach((modal) => {
      if (watched.has(modal)) return;
      watched.add(modal);
      observer.observe(modal);
    });
  };
  // cards added later (filters, infinite scroll, recommendations) are picked up after the page settles
  const scanSoon = () => idle(scan, { timeout: 3000 });
  if (document.readyState === 'complete') scanSoon();
  else window.addEventListener('load', scanSoon, { once: true });
  document.addEventListener('theme:content:loaded', scanSoon);
  document.addEventListener('scrollend', scanSoon, { passive: true });
})();
